import React, { useRef } from 'react';

const cx = (...names) => names.filter(Boolean).join(' ');

const STAR_PATH = 'm12 3 2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9z';
export const STEPS = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];

/**
 * One star filled to `fill` (0, 0.5 or 1): an outline under a clipped solid
 * star. Without `size` it takes the CSS size (--ui-star-size).
 */
function Star({ fill, size }) {
  const box = size ? { width: size, height: size } : undefined;
  return (
    <span className="ui-star" style={box} aria-hidden="true">
      <svg viewBox="0 0 24 24" style={box} className="ui-star__empty">
        <path d={STAR_PATH} />
      </svg>
      {fill > 0 && (
        <span className="ui-star__fill" style={{ width: `${fill * 100}%` }}>
          <svg viewBox="0 0 24 24" style={box}><path d={STAR_PATH} /></svg>
        </span>
      )}
    </span>
  );
}

const fillOf = (value, n) => {
  if (value == null) return 0;
  if (value >= n) return 1;
  return value >= n - 0.5 ? 0.5 : 0;
};

/** Five stars showing a value of 0 to 5 (rounded to halves); decorative — say the value in text beside it. */
export function StarBar({ value, size = 16, className }) {
  const rounded = value == null ? null : Math.round(value * 2) / 2;
  return (
    <span className={cx('ui-stars', className)} aria-hidden="true">
      {[1, 2, 3, 4, 5].map((n) => <Star key={n} fill={fillOf(rounded, n)} size={size} />)}
    </span>
  );
}

/**
 * A rating of 0.5 to 5 stars by halves, as a radio group of ten (spec
 * §2.4): a click on the left half of a star gives the half, on its right
 * half the whole; arrows step by halves, Home and End go to the ends,
 * Delete or Backspace clears; clicking the current value clears it too.
 * Only the checked radio (or the first) is in the tab order. `valueText(v)`
 * names each step ("3.5 stars"); onChange receives a number or null. Stars
 * are 30 px, 36 px on phones and touch screens, where each half is a 24 px
 * target (ui.css).
 */
export function StarRating({ value, onChange, label, valueText, disabled = false, className }) {
  const refs = useRef({});
  const current = STEPS.includes(value) ? value : null;
  const focusable = current ?? STEPS[0];

  const pick = (next, focus = false) => {
    if (disabled) return;
    onChange(next);
    if (focus && next != null && refs.current[next]) refs.current[next].focus();
  };

  const onKeyDown = (event) => {
    let next;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        next = current == null ? STEPS[0] : Math.min(5, current + 0.5);
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        next = current == null ? STEPS[0] : Math.max(0.5, current - 0.5);
        break;
      case 'Home':
        next = STEPS[0];
        break;
      case 'End':
        next = 5;
        break;
      case 'Delete':
      case 'Backspace':
        event.preventDefault();
        pick(null);
        // Cleared: the first half is the one in the tab order now; focus stays in the group.
        if (refs.current[STEPS[0]]) refs.current[STEPS[0]].focus();
        return;
      default:
        return;
    }
    event.preventDefault();
    if (next !== current) pick(next, true);
  };

  return (
    <div role="radiogroup" aria-label={label} aria-disabled={disabled || undefined}
         className={cx('ui-rating', disabled && 'is-disabled', className)} onKeyDown={onKeyDown}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className="ui-rating__star">
          <Star fill={fillOf(current, n)} />
          {[n - 0.5, n].map((step) => (
            <button
              key={step}
              ref={(node) => { refs.current[step] = node; }}
              type="button"
              role="radio"
              aria-checked={current === step}
              aria-label={valueText(step)}
              tabIndex={step === focusable ? 0 : -1}
              disabled={disabled}
              className={cx('ui-rating__half', step === n ? 'ui-rating__half--right' : 'ui-rating__half--left')}
              onClick={() => pick(current === step ? null : step, true)}
            />
          ))}
        </span>
      ))}
    </div>
  );
}

export default StarRating;
